/**
 * Composant de vue détaillée pour afficher les informations complètes d'un objet touristique
 */

import { LitElement, html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  ObjetTouristique,
  PeriodeOuverture,
  MoyenCommunication,
} from "../types/apidae.d";
import type { WidgetConfiguration } from "../types/widgets.d";
import { DisplayedFieldId } from "../types/widgets.d";
import { InformationBlock } from "./DetailView/InformationBlock";
import { InformationBlockListing } from "./DetailView/InformationBlockListing";
import { InformationBlockListingWColumns } from "./DetailView/InformationBlockListingWColumns";
import { InformationBlockDisclaimer } from "./DetailView/InformationBlockDisclaimer";
import { Icon } from "./Icon";
import { TranslationService } from "../services/TranslationService";
import { WidgetLanguage, WidgetFieldsInfos } from "../types/widgets.d";
import detailViewStyles from "../styles/detail-view.css?inline";
import { getDisplayedFieldData } from "../utils/displayedFieldUtils";
import { t } from "../utils/widgetsUtils";
import { getMultimediaTypeIconName } from "../config/multimediaTypeIcons";
import {
  renderAccessibilitySection,
  renderLocationAndContactSection,
  renderCyclistsAndPublicSection,
  renderEquipmentsSection,
  renderExtraDocumentationSection,
  renderItineraryAndTopoSection,
  renderLabelsSection,
  renderLongDescriptionSection,
  renderNewsAndOffersSection,
  renderOpeningSection,
  renderPaymentMethodsSection,
  renderPricingSection,
  renderReservationSection,
  renderRoomsAndAnimalsSection,
  renderCapabilitiesSection,
  renderSustainableAndKidsSection,
  renderAlertAndTipsSection,
  renderOtherDescriptifThematiseSection,
  renderMapSection,
  renderOpenSystemSection,
  renderEllohaSection,
} from "./DetailView/DetailViewSections";

// MapWidget n'est pas importé ici pour éviter une dépendance circulaire
// (BaseWidget → DetailView → MapWidget → BaseWidget). Il est chargé à la demande
// quand l'objet a des coordonnées (voir updated()).

// Import nécessaire pour enregistrer les composants
InformationBlock;
InformationBlockListing;
InformationBlockListingWColumns;
InformationBlockDisclaimer;
Icon;

@customElement("apidae-detail-view")
export class DetailView extends LitElement {
  @property({ type: Object })
  item!: ObjetTouristique;

  @property({ type: String, attribute: "language" })
  language: WidgetLanguage = WidgetLanguage.FR;

  @property({ type: Array })
  displayedFields: WidgetFieldsInfos[] = [];

  /** Configuration du widget parent (optionnel), utilisée pour la carte en bas de page */
  @property({ type: Object, attribute: false })
  widgetConfig: WidgetConfiguration | null = null;

  @state()
  currentOpeningPeriodIndex: number = 0;

  @state()
  currentPricingPeriodIndex: number = 0;

  @state()
  private photoGalleryView: boolean = false;

  /** Carte chargée à la demande pour éviter la dépendance circulaire avec BaseWidget (exposé au contexte des sections) */
  @state()
  mapWidgetLoaded: boolean = false;

  /** Index des illustrations en erreur de chargement (pour ne pas les réafficher) */
  @state()
  private failedImages: Set<number> = new Set();

  static styles: CSSResultGroup = unsafeCSS(detailViewStyles);

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("item")) {
      this.currentOpeningPeriodIndex = 0;
      this.currentPricingPeriodIndex = 0;
      this.failedImages = new Set();
    }
    const hasCoords =
      !!this.item?.localisation?.geolocalisation?.geoJson?.coordinates;
    if (hasCoords && !this.mapWidgetLoaded) {
      import("../widgets/MapWidget").then(() => {
        this.mapWidgetLoaded = true;
      });
    }
  }

  setCurrentOpeningPeriodIndex(index: number): void {
    this.currentOpeningPeriodIndex = Math.max(0, index);
    this.requestUpdate();
  }

  setCurrentPricingPeriodIndex(index: number): void {
    this.currentPricingPeriodIndex = Math.max(0, index);
    this.requestUpdate();
  }

  /** Fait défiler la fiche jusqu'à la section carte (conteneur .detail-overlay) avec animation */
  scrollToMapSection(): void {
    const section = this.renderRoot?.querySelector("#detail-map-section");
    const overlay = this.renderRoot?.querySelector(".detail-overlay");
    if (section && overlay) {
      const sectionTopInViewport = section.getBoundingClientRect().top;
      const overlayTopInViewport = overlay.getBoundingClientRect().top;
      const scrollTarget =
        overlay.scrollTop + sectionTopInViewport - overlayTopInViewport - 20;
      overlay.scrollTo({ top: Math.max(0, scrollTarget), behavior: "smooth" });
    } else {
      section?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent("close", { bubbles: true, composed: true }),
    );
  }

  private handleBackdropClick(e: Event) {
    if (e.target === e.currentTarget) {
      this.handleClose();
    }
  }

  private _boundKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.handleClose();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this._boundKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._boundKeydown);
    super.disconnectedCallback();
  }

  // Méthodes utilitaires inspirées de ListWidget
  getLabel(libelle: any): string {
    return TranslationService.getLabelWithFallback(libelle, this.language);
  }

  formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
    return TranslationService.formatDate(dateStr, this.language, options);
  }

  getCoordonneesValue(coordonnees: {
    fr?: string;
    en?: string;
    es?: string;
    it?: string;
    de?: string;
    nl?: string;
    ru?: string;
    zh?: string;
    ptBR?: string;
    ja?: string;
    ko?: string;
  }): string | null {
    if (!coordonnees) return null;

    const langMap: Record<string, keyof typeof coordonnees> = {
      [WidgetLanguage.FR]: "fr",
      [WidgetLanguage.EN]: "en",
      [WidgetLanguage.ES]: "es",
      [WidgetLanguage.IT]: "it",
      [WidgetLanguage.DE]: "de",
      [WidgetLanguage.NL]: "nl",
      [WidgetLanguage.RU]: "ru",
      [WidgetLanguage.ZH]: "zh",
      [WidgetLanguage.PT_BR]: "ptBR",
      [WidgetLanguage.JA]: "ja",
      [WidgetLanguage.KO]: "ko",
    };

    const key = langMap[this.language];
    if (key && coordonnees[key]) {
      return coordonnees[key] || null;
    }

    if (coordonnees.fr) {
      return coordonnees.fr;
    }

    const values = Object.values(coordonnees).filter((v) => v);
    return values.length > 0 ? values[0] : null;
  }

  formatOuverturePeriod(periode: PeriodeOuverture): string | null {
    if (!periode.dateDebut) return null;

    const dateDebut = this.formatDate(periode.dateDebut, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    if (periode.dateFin) {
      const dateFin = this.formatDate(periode.dateFin, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      return `Du ${dateDebut} au ${dateFin}`;
    } else {
      return `Du ${dateDebut}`;
    }
  }

  getTelephones(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 201,
      ) || []
    );
  }

  getEmails(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 204,
      ) || []
    );
  }

  getWebsites(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 205,
      ) || []
    );
  }

  getReservationUrl(): string | null {
    const website = this.getWebsites()[0];
    if (!website) return null;
    return this.getCoordonneesValue(website.coordonnees);
  }

  /**
   * Vérifie si un champ doit être affiché selon la configuration
   */
  shouldDisplayField(fieldId: string): boolean {
    if (!this.item) return false;

    const fieldConfig = this.displayedFields
      ? this.displayedFields.find((field) => field.id === fieldId)
      : undefined;

    // N'afficher que si le DisplayedFieldId est dans la config et qu'il y a des données
    const hasConfig = !!this.displayedFields && this.displayedFields.length > 0;
    if (!hasConfig || !fieldConfig) {
      return false;
    }

    const data = getDisplayedFieldData(this.item, fieldId, {
      language: this.language,
      getLabel: (libelle) => this.getLabel(libelle),
      formatDate: (dateStr, options) => this.formatDate(dateStr, options),
      t: (key) => t(key, this.language),
      fieldConfig,
    });

    return data !== null;
  }

  /**
   * Retourne l'icône appropriée selon le type de multimédia (API Apidae / Sitra).
   */
  getMultimediaIcon(type: string): string {
    return getMultimediaTypeIconName(type);
  }

  private formatJourSemaine(jour: string): string {
    const jours: Record<string, string> = {
      LUNDI: "Lundi",
      MARDI: "Mardi",
      MERCREDI: "Mercredi",
      JEUDI: "Jeudi",
      VENDREDI: "Vendredi",
      SAMEDI: "Samedi",
      DIMANCHE: "Dimanche",
    };
    return jours[jour] || jour;
  }

  getOuvertureItems(periode: PeriodeOuverture): Array<{
    icon: string;
    title: string;
    content: string;
  }> {
    const items: Array<{ icon: string; title: string; content: string }> = [];

    // Si complementHoraire existe, l'utiliser
    if (periode.complementHoraire) {
      const horaire = this.getLabel(periode.complementHoraire);
      // Essayer de parser les jours depuis ouverturesJournalieres
      if ((periode as any).ouverturesJournalieres) {
        const jours = (periode as any).ouverturesJournalieres.map((oj: any) =>
          this.formatJourSemaine(oj.jour),
        );
        if (jours.length > 0) {
          jours.forEach((jour: string) => {
            items.push({
              icon: "bold/time/clock_square",
              title: jour,
              content: horaire,
            });
          });
        } else {
          items.push({
            icon: "bold/time/clock_square",
            title: "Horaires",
            content: horaire,
          });
        }
      } else {
        items.push({
          icon: "bold/time/clock_square",
          title: "Horaires",
          content: horaire,
        });
      }
    } else if ((periode as any).ouverturesJournalieres) {
      // Si pas de complementHoraire mais des ouverturesJournalieres
      const jours = (periode as any).ouverturesJournalieres.map((oj: any) =>
        this.formatJourSemaine(oj.jour),
      );
      jours.forEach((jour: string) => {
        items.push({
          icon: "bold/time/clock_square",
          title: jour,
          content: "Sur réservation",
        });
      });
    }

    return items;
  }

  getPeriodesGroupedByYear(): Map<number, PeriodeOuverture[]> {
    const periodesByYear = new Map<number, PeriodeOuverture[]>();

    if (!this.item.ouverture?.periodesOuvertures) {
      return periodesByYear;
    }

    this.item.ouverture.periodesOuvertures.forEach((periode) => {
      if (!periode.dateDebut) return;

      const dateDebut = new Date(periode.dateDebut);
      const year = dateDebut.getFullYear();

      // Si tousLesAns est true, ajouter pour les années courante et suivante
      const periodeAny = periode as any;
      if (periodeAny.tousLesAns) {
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y <= currentYear + 1; y++) {
          if (!periodesByYear.has(y)) {
            periodesByYear.set(y, []);
          }
          periodesByYear.get(y)!.push(periode);
        }
      } else {
        if (!periodesByYear.has(year)) {
          periodesByYear.set(year, []);
        }
        periodesByYear.get(year)!.push(periode);
      }
    });

    return periodesByYear;
  }

  /** Retourne les images depuis le case DisplayedFieldId.IMAGES (comme les autres sections), en excluant celles en erreur de chargement. */
  private getHeaderImageItems(): Array<{ url: string; index: number }> {
    if (!this.item) return [];
    const fieldConfig = this.displayedFields?.find(
      (f) => f.id === DisplayedFieldId.IMAGES,
    );
    const data = getDisplayedFieldData(this.item, DisplayedFieldId.IMAGES, {
      language: this.language,
      getLabel: (libelle) => this.getLabel(libelle),
      formatDate: (dateStr, options) => this.formatDate(dateStr, options),
      t: (key) => t(key, this.language),
      fieldConfig,
    });
    if (!data?.items?.length) return [];
    return (data.items as Array<{ url?: string | null }>)
      .map((item, idx) => ({
        url: item.url ?? null,
        index: idx,
      }))
      .filter(
        (i): i is { url: string; index: number } =>
          !!i.url && !this.failedImages.has(i.index),
      );
  }

  private handleHeaderImageError(index: number) {
    this.failedImages = new Set(this.failedImages).add(index);
    this.requestUpdate();
  }

  private openPhotoGallery() {
    this.photoGalleryView = true;
  }

  private closePhotoGallery() {
    this.photoGalleryView = false;
  }

  private renderPhotoGalleryView(
    imageItems: Array<{ url: string; index: number }>,
  ) {
    const pattern: number[] = [1, 2, 1];
    let index = 0;
    const gridRows: { indices: number[]; height: string }[] = [];
    while (index < imageItems.length) {
      for (let p = 0; p < pattern.length && index < imageItems.length; p++) {
        const n = pattern[p];
        const indices = [];
        for (let j = 0; j < n && index < imageItems.length; j++)
          indices.push(index++);
        const isFirstRowOfTriple = p === 0;
        gridRows.push({
          indices,
          height:
            indices.length === 1 && isFirstRowOfTriple ? "600px" : "275px",
        });
      }
    }
    return html`
      <div class="detail-photo-visit">
        <h2 class="detail-photo-visit__title">
          ${t("photoVisit", this.language)}
        </h2>
        <div class="detail-photo-visit__grid">
          ${gridRows.map(
            (row) => html`
              <div
                class="detail-photo-visit__row detail-photo-visit__row--${row
                  .indices.length}"
                style="height: ${row.height}"
              >
                ${row.indices.map(
                  (i) => html`
                    <div class="detail-photo-visit__cell">
                      <img
                        class="detail-photo-visit__img"
                        src="${imageItems[i].url}"
                        alt=""
                        loading="lazy"
                        @error=${() =>
                          this.handleHeaderImageError(imageItems[i].index)}
                      />
                    </div>
                  `,
                )}
              </div>
            `,
          )}
        </div>
        <apidae-button
          variant="secondary100"
          class="detail-photo-visit__back"
          @click=${this.closePhotoGallery}
        >
          <apidae-icon
            name="bold/arrows/round_arrow_left"
            size="22"
            color="currentColor"
          ></apidae-icon>
          <span style="margin-left: 8px"
            >${t("backToFiche", this.language)}</span
          >
        </apidae-button>
      </div>
    `;
  }

  render() {
    const allImageItems = this.getHeaderImageItems();
    // Exception images : sans DisplayedFieldId.IMAGES on n'affiche que la première image
    const imageItems = this.shouldDisplayField(DisplayedFieldId.IMAGES)
      ? allImageItems
      : allImageItems.slice(0, 1);
    const hasImages = imageItems.length > 0;
    const showSeeMore = imageItems.length > 3;

    return html`
      <div
        class="detail-overlay"
        @click=${this.handleBackdropClick}
        role="presentation"
      >
        <div class="detail-close-wrapper">
          <button
            class="close-button"
            @click=${this.handleClose}
            title="Fermer"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div
          class="detail-container"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="detail-slider">
            <div
              class="detail-pane detail-pane--detail"
              ?data-active=${!this.photoGalleryView}
            >
              <div class="detail-header-block">
                <div
                  class="detail-header ${hasImages
                    ? `detail-header-gallery detail-header-gallery--${Math.min(imageItems.length, 3)}`
                    : ""}"
                >
                  ${!hasImages
                    ? html`<div
                        class="detail-image-placeholder"
                        aria-hidden="true"
                      >
                        ${(this.getLabel(this.item?.nom) || "")
                          .charAt(0)
                          .toUpperCase() || ""}
                      </div>`
                    : imageItems.length === 1
                      ? html`
                          <div class="detail-header-gallery__single">
                            <img
                              class="detail-image"
                              src="${imageItems[0].url}"
                              alt=""
                              loading="lazy"
                              @error=${() =>
                                this.handleHeaderImageError(
                                  imageItems[0].index,
                                )}
                            />
                          </div>
                        `
                      : imageItems.length === 2
                        ? html`
                            <div class="detail-header-gallery__left">
                              <img
                                class="detail-image"
                                src="${imageItems[0].url}"
                                alt=""
                                loading="lazy"
                                @error=${() =>
                                  this.handleHeaderImageError(
                                    imageItems[0].index,
                                  )}
                              />
                            </div>
                            <div class="detail-header-gallery__right">
                              <img
                                class="detail-image"
                                src="${imageItems[1].url}"
                                alt=""
                                loading="lazy"
                                @error=${() =>
                                  this.handleHeaderImageError(
                                    imageItems[1].index,
                                  )}
                              />
                            </div>
                          `
                        : html`
                            <div class="detail-header-gallery__left">
                              <img
                                class="detail-image"
                                src="${imageItems[0].url}"
                                alt=""
                                loading="lazy"
                                @error=${() =>
                                  this.handleHeaderImageError(
                                    imageItems[0].index,
                                  )}
                              />
                            </div>
                            <div class="detail-header-gallery__right">
                              <div class="detail-header-gallery__right-top">
                                <img
                                  class="detail-image"
                                  src="${imageItems[1].url}"
                                  alt=""
                                  loading="lazy"
                                  @error=${() =>
                                    this.handleHeaderImageError(
                                      imageItems[1].index,
                                    )}
                                />
                              </div>
                              <div class="detail-header-gallery__right-bottom">
                                <img
                                  class="detail-image"
                                  src="${imageItems[2].url}"
                                  alt=""
                                  loading="lazy"
                                  @error=${() =>
                                    this.handleHeaderImageError(
                                      imageItems[2].index,
                                    )}
                                />
                                ${showSeeMore
                                  ? html`
                                      <apidae-button
                                        variant="secondary100"
                                        class="detail-header-gallery__see-more"
                                        @click=${this.openPhotoGallery}
                                        aria-label="${t(
                                          "showMore",
                                          this.language,
                                        )}"
                                      >
                                        ${t("showMore", this.language)}
                                        <apidae-icon
                                          name="bold/arrows/round_arrow_right"
                                          size="22"
                                          color="currentColor"
                                          slot="icon"
                                        ></apidae-icon>
                                      </apidae-button>
                                    `
                                  : ""}
                              </div>
                            </div>
                          `}
                </div>
                <h1 class="detail-header-title">
                  ${this.getLabel(this.item?.nom) || ""}
                </h1>
              </div>

              <div class="detail-content detail-content--slide">
                ${renderLocationAndContactSection(this)}
                ${renderOpeningSection(this)}
                ${renderLongDescriptionSection(this)}
                ${renderPricingSection(this)}
                ${renderPaymentMethodsSection(this)}
                ${renderReservationSection(this)}
                ${renderExtraDocumentationSection(this)}
                ${renderEquipmentsSection(this)} ${renderLabelsSection(this)}
                ${renderAccessibilitySection(this)}
                ${renderRoomsAndAnimalsSection(this)}
                ${renderCapabilitiesSection(this)}
                ${renderAlertAndTipsSection(this)}
                ${renderItineraryAndTopoSection(this)}
                ${renderSustainableAndKidsSection(this)}
                ${renderNewsAndOffersSection(this)}
                ${renderCyclistsAndPublicSection(this)}
                ${renderOtherDescriptifThematiseSection(this)}
                ${renderMapSection(this)} ${renderOpenSystemSection(this)}
                ${renderEllohaSection(this)}
              </div>
            </div>
            <div
              class="detail-pane detail-pane--photo"
              ?data-active=${this.photoGalleryView}
            >
              ${this.renderPhotoGalleryView(imageItems)}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-detail-view": DetailView;
  }
}
